import { useState, useCallback, useRef } from 'react';
import { SelectionState, Vendor } from '../types/types';
import { generateDisplayIds } from '../lib/displayUtils';

export const useVendorSelection = (
  vendors: Vendor[],
  maxApiDaysRange: number
): {
  selection: SelectionState;
  handleVendorSelect: (vendorId: number, fromDisplayId?: string, forceSelect?: boolean) => void;
  resetSelection: () => void;
} => {
  const [selection, setSelection] = useState<SelectionState>({
    vendorId: null,
    currentDateDisplayId: null,
    allDisplayIds: [],
  });

  const lastClickRef = useRef<{ vendorId: number; timestamp: number } | null>(null);

  const handleVendorSelect = useCallback(
    (vendorId: number, fromDisplayId?: string, forceSelect = false) => {
      const now = Date.now();

      // Debounce logic
      if (
        lastClickRef.current?.vendorId === vendorId &&
        now - lastClickRef.current.timestamp < 500
      ) {
        return;
      }

      lastClickRef.current = { vendorId, timestamp: now };

      setSelection(currentSelection => {
        // Only deselect if same vendor AND not forced (i.e., from panel, not popup)
        if (currentSelection.vendorId === vendorId && !forceSelect) {
          return { vendorId: null, currentDateDisplayId: null, allDisplayIds: [] };
        }

        // Find vendor and generate display IDs
        const vendor = vendors.find(v => v.id === vendorId);
        if (!vendor) return currentSelection;

        if (fromDisplayId) {
          return {
            vendorId,
            currentDateDisplayId: fromDisplayId,
            allDisplayIds: [fromDisplayId],
          };
        }

        const allDisplayIds = generateDisplayIds(vendor, maxApiDaysRange);
        const currentDateDisplayId = fromDisplayId || allDisplayIds[0];

        return { vendorId, currentDateDisplayId, allDisplayIds };
      });
    },
    [vendors, maxApiDaysRange]
  );

  const resetSelection = useCallback(() => {
    setSelection({
      vendorId: null,
      currentDateDisplayId: null,
      allDisplayIds: [],
    });
  }, []);

  return { selection, handleVendorSelect, resetSelection };
};
